import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Award, CheckCircle, XCircle, Clock } from 'lucide-react';
import Header from './Header';
import { formatDateTime } from '../utils/dateFormatter';

// Using relative URLs

const TrainingResult = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrainingResult();
  }, [trainingId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTrainingResult = async () => {
    try {
      const response = await axios.get(
        `/api/trainings/${trainingId}/my-completion`,
        getAuthHeaders()
      );
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load training results');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getUserAnswer = (questionId) => {
    const answer = data.completion.answers.find(a => a.question_id === questionId);
    return answer?.answer;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Training Results Not Found</h2>
          <Button onClick={() => navigate('/employee/portal')}>Back to Portal</Button>
        </div>
      </div>
    );
  }

  const { training, completion } = data;

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/employee/portal')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Portal
        </Button>

        {/* Result Summary Card */}
        <Card className={`mb-6 ${completion.passed ? 'border-navy-500' : 'border-orange-500'}`}>
          <CardHeader>
            <div className="text-center">
              {completion.passed ? (
                <>
                  <Award className="w-16 h-16 mx-auto mb-4 text-navy-600" />
                  <CardTitle className="text-3xl text-navy-600">Training Completed</CardTitle>
                  <CardDescription className="text-lg mt-2">You passed this training!</CardDescription>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 mx-auto mb-4 text-orange-600" />
                  <CardTitle className="text-3xl text-orange-600">Training Completed</CardTitle>
                  <CardDescription className="text-lg mt-2">Review your results below</CardDescription>
                </>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{training.title}</h3>
              {training.description && (
                <p className="text-slate-600">{training.description}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-slate-900">{completion.score}%</p>
                <p className="text-sm text-slate-600">Score</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-3xl font-bold text-navy-600">
                  {training.questions.filter(q => getUserAnswer(q.id) === q.correct_answer).length}
                </p>
                <p className="text-sm text-slate-600">Correct</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-slate-900">{training.questions.length}</p>
                <p className="text-sm text-slate-600">Total</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 text-sm text-slate-600">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {training.duration_minutes} minutes
              </div>
              <span>•</span>
              <span>Completed on {formatDateTime(completion.completed_at)}</span>
            </div>

            {training.frameworks && training.frameworks.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {training.frameworks.map((fw) => (
                  <Badge key={fw} variant="outline">{fw}</Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Training Content */}
        {training.content && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Training Material</CardTitle>
              <CardDescription>Review the training content</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="prose max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap">{training.content}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions and Answers Review */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Questions & Your Answers</CardTitle>
            <CardDescription>Review what you answered</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {training.questions.map((question, index) => {
              const userAnswer = getUserAnswer(question.id);
              const isCorrect = userAnswer === question.correct_answer;

              return (
                <div key={question.id} className={`p-4 border-2 rounded-lg ${
                  isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-orange-200 bg-orange-50'
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <p className="font-semibold text-slate-900 flex-1">
                      {index + 1}. {question.question}
                    </p>
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-navy-600 ml-2" />
                    ) : (
                      <XCircle className="w-5 h-5 text-orange-600 ml-2" />
                    )}
                  </div>

                  <div className="space-y-2 ml-4">
                    {Object.entries(question.options).map(([key, value]) => {
                      const isUserAnswer = key === userAnswer;
                      const isCorrectAnswer = key === question.correct_answer;

                      return (
                        <div 
                          key={key} 
                          className={`p-2 rounded ${
                            isCorrectAnswer 
                              ? 'bg-emerald-100 border border-emerald-300' 
                              : isUserAnswer 
                              ? 'bg-orange-100 border border-orange-300' 
                              : ''
                          }`}
                        >
                          <span className={`font-medium mr-2 ${
                            isCorrectAnswer ? 'text-navy-700' : 
                            isUserAnswer ? 'text-orange-700' : 'text-slate-600'
                          }`}>
                            {key}.
                          </span>
                          <span className={
                            isCorrectAnswer ? 'text-navy-700 font-medium' : 
                            isUserAnswer ? 'text-orange-700' : 'text-slate-700'
                          }>
                            {value}
                            {isUserAnswer && !isCorrectAnswer && ' (Your answer)'}
                            {isCorrectAnswer && ' ✓ (Correct answer)'}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {question.explanation && (
                    <div className="mt-3 ml-4 p-3 bg-white rounded border border-slate-200">
                      <p className="text-sm text-slate-600 italic">
                        <strong>Explanation:</strong> {question.explanation}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="text-center">
          <Button
            onClick={() => navigate('/employee/portal')}
            className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800"
          >
            Back to Portal
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrainingResult;
